import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Button,
  FormControlLabel,
  FormLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  validateString,
  validationLength,
  validationType,
  validationMessages,
} from "../../actions/validations";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import { sampleTestBlockMessage } from "../../../constants/sncSampleGuard";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { buttonValues } from "../../../constants/Types";
import { SnCPropertyEnum } from "../../../constants/NavbarList";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import AlertComponent from "../../subcomponents/AlertComponent";
import { sncManagersTooltip } from "../../../constants/SnCTooltipInformation";
import { useTooltipText } from "../../../tooltips/TooltipContext";

const MANAGER_TYPE_OPTIONS = [
  "CATERINGMANAGER",
  "CONFERENCESERVICEMANAGER",
  "SALESMANAGER",
] as const;

type ManagerType = (typeof MANAGER_TYPE_OPTIONS)[number];

const FIELD = {
  MAPPING_CODE: "MAPPING_CODE",
  MANAGER_TYPE: "MANAGER_TYPE",
  ISACTIVE: "ISACTIVE",
  FIRST_NAME: "MANAGER_FIRSTNAME",
  LAST_NAME: "MANAGER_LASTNAME",
} as const;

const TOOLTIP_GROUP = "sncManagersTooltip";

const SnCManagers = ({
  data,
  fieldValues,
}: {
  data: (value: any) => void;
  fieldValues: any;
}) => {
  const tipHeader = useTooltipText(
    TOOLTIP_GROUP,
    "header",
    sncManagersTooltip.header,
  );
  const tipMappingCode = useTooltipText(
    TOOLTIP_GROUP,
    "mappingCode",
    sncManagersTooltip.mappingCode,
  );
  const tipManagerType = useTooltipText(
    TOOLTIP_GROUP,
    "managerType",
    sncManagersTooltip.managerType,
  );
  const tipIsActive = useTooltipText(
    TOOLTIP_GROUP,
    "isActive",
    sncManagersTooltip.isActive,
  );
  const tipFirstName = useTooltipText(
    TOOLTIP_GROUP,
    "managerFirstName",
    sncManagersTooltip.managerFirstName,
  );
  const tipLastName = useTooltipText(
    TOOLTIP_GROUP,
    "managerLastName",
    sncManagersTooltip.managerLastName,
  );
  const [mappingCode, setMappingCode] = useState<string | null>(null);
  const [managerType, setManagerType] = useState<ManagerType | null>(null);
  const [isActive, setIsActive] = useState<boolean>(true);
  const [firstName, setFirstName] = useState<string | null>(null);
  const [lastName, setLastName] = useState<string | null>(null);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [items, setItems] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const [mappingCodeErr, setMappingCodeErr] = useState("");
  const [managerTypeErr, setManagerTypeErr] = useState(false);
  const [firstNameErr, setFirstNameErr] = useState("");
  const [lastNameErr, setLastNameErr] = useState("");

  useEffect(() => {
    fieldValues && setItems(Array.isArray(fieldValues) ? fieldValues : []);
  }, []);

  useEffect(() => {
    data(items);
    setSelectedId(checkData(items, selectedId) ? selectedId : null);
  }, [items]);

  const payload = {
    [FIELD.MAPPING_CODE]: mappingCode,
    [FIELD.MANAGER_TYPE]: managerType,
    [FIELD.ISACTIVE]: isActive,
    [FIELD.FIRST_NAME]: firstName,
    [FIELD.LAST_NAME]: lastName,
  };

  const clearFieldErrors = () => {
    setMappingCodeErr("");
    setManagerTypeErr(false);
    setFirstNameErr("");
    setLastNameErr("");
  };

  const validateInput = (): boolean => {
    clearFieldErrors();
    let ok = true;

    if (!mappingCode) {
      setMappingCodeErr(
        "Manager Code" + validationMessages.sncMandatorySuffix,
      );
      ok = false;
    }
    if (!managerType) {
      setManagerTypeErr(true);
      ok = false;
    }
    if (!firstName) {
      setFirstNameErr(
        "First Name" + validationMessages.sncMandatorySuffix,
      );
      ok = false;
    }
    if (!lastName) {
      setLastNameErr("Last Name" + validationMessages.sncMandatorySuffix);
      ok = false;
    }
    if (!ok) {
      setAlertMessage(validationMessages.sncAllMandatory);
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    if (
      !validateString(
        mappingCode,
        validationLength.sncManagerCode,
        validationType.NOSPACE,
      )
    ) {
      setMappingCodeErr(validationMessages.sncManagerCode);
      return false;
    }
    if (
      !validateString(
        firstName,
        validationLength.sncManagerName,
        validationType.NOSPACE,
      )
    ) {
      setFirstNameErr(validationMessages.sncManagerName);
      return false;
    }
    if (
      !validateString(
        lastName,
        validationLength.sncManagerName,
        validationType.NOSPACE,
      )
    ) {
      setLastNameErr(validationMessages.sncManagerName);
      return false;
    }
    const sampleBlock = sampleTestBlockMessage(
      mappingCode,
      firstName,
      lastName,
      managerType,
    );
    if (sampleBlock) {
      setAlertMessage(sampleBlock);
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    return true;
  };

  const updateItem = () => {
    if (!validateInput()) {
      return;
    }
    const otherItems = (items ?? []).filter(
      (item: any) => item.id !== selectedId,
    );
    if (checkDuplicate(otherItems, FIELD.MAPPING_CODE, mappingCode)) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }

    setItems((prev) =>
      (prev ?? []).map((item: any) =>
        item.id === selectedId ? { ...item, ...payload } : item,
      ),
    );
    editItem(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${SnCPropertyEnum.managers} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const addItem = () => {
    if (!validateInput()) {
      return;
    }
    if (checkDuplicate(items ?? [], FIELD.MAPPING_CODE, mappingCode)) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }

    setItems((prev) =>
      updateData(
        prev?.length
          ? [...prev, { id: (prev?.length ?? 0) + 1, ...payload }]
          : [{ id: 1, ...payload }],
      ),
    );
    setLastUpdatedRecord(payload);
    editItem(null);
    setEditClicked(false);
  };

  const editItem = (list: any) => {
    list?.id && setSelectedId(list.id);
    if (list != null) {
      setEditClicked(true);
      clearFieldErrors();
      setMappingCode(list[FIELD.MAPPING_CODE] ?? null);
      setManagerType(
        MANAGER_TYPE_OPTIONS.includes(list[FIELD.MANAGER_TYPE])
          ? list[FIELD.MANAGER_TYPE]
          : null,
      );
      setIsActive(list[FIELD.ISACTIVE] !== false);
      setFirstName(list[FIELD.FIRST_NAME] ?? null);
      setLastName(list[FIELD.LAST_NAME] ?? null);
    } else {
      setMappingCode(null);
      setManagerType(null);
      setIsActive(true);
      setFirstName(null);
      setLastName(null);
      clearFieldErrors();
    }
  };

  const deleteItemById = (id: number) => {
    setItems(deleteItem(items ?? [], id));
  };

  return (
    <Stack direction="column" gap="10px">
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "20px",
        }}
      >
        <Stack
          direction="row"
          width="100%"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" sx={textStyle}>
            Managers
            <Tooltip title={tooltipComponent(tipHeader)}>
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Typography>
          <Stack direction="row" gap="10px">
            {editClicked && (
              <ClearButton
                click={() => {
                  editItem(null);
                  setSelectedId(null);
                }}
              />
            )}
            {lastUpdatedRecord && (
              <CopyButton click={() => editItem(lastUpdatedRecord)} />
            )}
          </Stack>
        </Stack>
        <Stack direction="column" width="100%" gap="20px">
          <Stack
            direction="row"
            className="form-two-col-row"
            flexWrap="wrap"
            gap="20px"
            alignItems="flex-start"
          >
            <Stack direction="column" className="form-two-col-field">
              <FormLabel component="legend">
                Manager Code *
                <Tooltip title={tooltipComponent(tipMappingCode)}>
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                required
                variant="outlined"
                size="small"
                placeholder="MAPPING_CODE"
                value={mappingCode ?? ""}
                error={!!mappingCodeErr}
                helperText={mappingCodeErr}
                inputProps={{ maxLength: validationLength.sncManagerCode }}
                onChange={(e) => {
                  setMappingCode(e.target.value || null);
                  setMappingCodeErr("");
                }}
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <FormLabel component="legend">
                Manager Type *
                <Tooltip title={tooltipComponent(tipManagerType)}>
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <Autocomplete
                options={[...MANAGER_TYPE_OPTIONS]}
                value={managerType}
                onChange={(_e, value) => {
                  setManagerType(value);
                  setManagerTypeErr(false);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="MANAGER_TYPE"
                    error={managerTypeErr}
                    helperText={
                      managerTypeErr
                        ? "MANAGER_TYPE" + validationMessages.sncMandatorySuffix
                        : ""
                    }
                  />
                )}
              />
            </Stack>
          </Stack>
          <Stack
            direction="row"
            className="form-two-col-row"
            flexWrap="wrap"
            gap="20px"
            alignItems="flex-start"
          >
            <Stack direction="column" className="form-two-col-field">
              <FormLabel component="legend">
                ISACTIVE
                <Tooltip title={tooltipComponent(tipIsActive)}>
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <FormControlLabel
                control={
                  <Switch
                    checked={isActive}
                    onChange={() => setIsActive(!isActive)}
                  />
                }
                label={isActive ? "Active" : "Inactive"}
              />
            </Stack>
          </Stack>
          <Stack
            direction="row"
            className="form-two-col-row"
            flexWrap="wrap"
            gap="20px"
          >
            <Stack direction="column" className="form-two-col-field">
              <FormLabel component="legend">
                First Name *
                <Tooltip title={tooltipComponent(tipFirstName)}>
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                required
                variant="outlined"
                size="small"
                placeholder="MANAGER_FIRSTNAME"
                value={firstName ?? ""}
                error={!!firstNameErr}
                helperText={firstNameErr}
                inputProps={{ maxLength: validationLength.sncManagerName }}
                onChange={(e) => {
                  setFirstName(e.target.value || null);
                  setFirstNameErr("");
                }}
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <FormLabel component="legend">
                Last Name *
                <Tooltip title={tooltipComponent(tipLastName)}>
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                required
                variant="outlined"
                size="small"
                placeholder="MANAGER_LASTNAME"
                value={lastName ?? ""}
                error={!!lastNameErr}
                helperText={lastNameErr}
                inputProps={{ maxLength: validationLength.sncManagerName }}
                onChange={(e) => {
                  setLastName(e.target.value || null);
                  setLastNameErr("");
                }}
              />
            </Stack>
          </Stack>
        </Stack>
        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            sx={{
              cursor: "pointer",
              border: "1px solid gray",
              borderRadius: "10px",
              background: "darkgreen",
              color: "white",
            }}
            onClick={() => (selectedId ? updateItem() : addItem())}
          >
            {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Manager
          </Button>
        </Stack>
        <Stack direction="column" gap="10px" mt={4}>
          <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
            Do not use sample data. Those are for demo purposes only!
          </Typography>
          <Typography variant="h6">Managers Available</Typography>
          {items?.length ? (
            <DraggableItemList
              itemData={items}
              onOrderUpdate={(order) => setItems(order)}
              onEdit={editItem}
              onDelete={deleteItemById}
              code={FIELD.MAPPING_CODE}
              name={FIELD.FIRST_NAME}
            />
          ) : (
            <Typography variant="body2">No Managers Available</Typography>
          )}
        </Stack>
      </Paper>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default SnCManagers;
